import geckos, { type ClientChannel } from '@geckos.io/client'

import {
  NET_PROTOCOL_VERSION,
  type ClientSpawn,
  type ClientWeaponDrop,
  type ClientWeaponPickup,
  type NetEvent,
  type PlayerInput,
  type PlayerSnapshot,
  type ServerWelcome,
  type WorldSnapshot,
} from '@/net/protocol'

interface BufferedSnapshot {
  serverTime: number
  players: Map<string, PlayerSnapshot>
}

export type NetStatus =
  | 'connecting'
  | 'connected'
  | 'reconnecting'
  | 'disconnected'

/** Stable per-tab id so reconnects reclaim this player without fighting other tabs. */
function loadSessionId(): string {
  const KEY = 'cs-tab-session-id'
  try {
    let id = sessionStorage.getItem(KEY)
    if (!id) {
      id = crypto.randomUUID()
      sessionStorage.setItem(KEY, id)
    }
    return id
  } catch {
    return crypto.randomUUID()
  }
}

const RECONNECT_DELAY_MS = 1200

const safeEmit = (
  channel: ClientChannel,
  event: string,
  data: Parameters<ClientChannel['emit']>[1],
): boolean => {
  try {
    channel.emit(event, data)
    return true
  } catch (error) {
    console.warn(`[net] emit failed (${event}):`, error)
    return false
  }
}

export default class NetClient {
  public id = ''
  public connected = false
  public status: NetStatus = 'connecting'
  public onStatus?: (status: NetStatus) => void
  public onReconnect?: () => void // a reconnect succeeded — resync local state
  public lastProcessedInputSeq = 0
  public rtt = 0
  public serverTime = 0
  public latestSelf?: PlayerSnapshot
  public latestPlayers: PlayerSnapshot[] = [] // all players from the last snapshot (scoreboard)
  public readonly pendingInputs: PlayerInput[] = []
  public readonly events: NetEvent[] = []

  private channel?: ClientChannel
  private interpolationDelay = 0.1
  private lastSnapshotAt = 0
  private readonly snapshots: BufferedSnapshot[] = []

  private readonly sessionId = loadSessionId()
  private url = ''
  private port = 0
  private closing = false // intentional disconnect (pagehide) — don't reconnect
  private everConnected = false
  private reconnectTimer?: number
  private pingTimer?: number

  // Dev: geckos server on :9208 (pnpm server:dev). Production: the signaling goes
  // through Traefik on the page's own origin (https → 443), routed to the geckos
  // pod at /.wrtc; the WebRTC media is UDP straight to the node's public IP. This
  // keeps everything under one domain and avoids mixed-content on HTTPS.
  public connect(
    url = `${location.protocol}//${location.hostname}`,
    port = location.hostname === 'localhost' || location.hostname === '127.0.0.1'
      ? 9208
      : location.protocol === 'https:'
        ? 443
        : 80,
  ): void {
    this.url = url
    this.port = port
    this.closing = false
    this.setStatus('connecting')
    this.startPingLoop()
    this.openChannel()
  }

  private openChannel(): void {
    const channel = geckos({ url: this.url, port: this.port })
    this.channel = channel

    channel.onConnect((error) => {
      // Ignore a callback from a channel we've already superseded.
      if (channel !== this.channel) return
      if (error) {
        console.error('[net] connection failed', error)
        this.scheduleReconnect()
        return
      }
      const reconnected = this.everConnected
      this.everConnected = true
      this.setStatus('connected')
      if (!safeEmit(channel, 'hello', {
        version: NET_PROTOCOL_VERSION,
        sessionId: this.sessionId,
      })) {
        this.setStatus('reconnecting')
        this.scheduleReconnect()
        return
      }
      if (reconnected) {
        // Drop the stale interpolation buffer so remote players don't jump from
        // pre-disconnect positions; let the controller resync the local player.
        this.snapshots.length = 0
        this.onReconnect?.()
      }
    })

    channel.on('welcome', (data) => {
      const welcome = data as ServerWelcome
      this.id = welcome.id
      this.interpolationDelay = welcome.interpolationDelayMs / 1000
    })

    channel.on('snapshot', (data) => this.applySnapshot(data as WorldSnapshot))
    channel.on('pong', (data) => {
      const pong = data as { clientTime: number; serverTime: number }
      this.rtt = performance.now() / 1000 - pong.clientTime
    })

    channel.onDisconnect(() => {
      // A superseded channel (closed during a reconnect) fires onDisconnect
      // asynchronously — ignore it, or it would tear down the live channel and
      // flap forever. Only the CURRENT channel disconnecting should reconnect.
      if (this.closing || channel !== this.channel) return
      this.setStatus('reconnecting')
      this.scheduleReconnect()
    })
  }

  private scheduleReconnect(): void {
    if (this.closing || this.reconnectTimer !== undefined) return
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = undefined
      // Null the ref BEFORE closing so the old channel's onDisconnect (which may
      // fire synchronously from close()) sees it's no longer current and bails.
      const old = this.channel
      this.channel = undefined
      this.closeChannel(old)
      this.openChannel()
    }, RECONNECT_DELAY_MS)
  }

  private setStatus(status: NetStatus): void {
    if (this.status === status) return
    this.status = status
    this.connected = status === 'connected'
    this.onStatus?.(status)
  }

  /** One persistent ~1Hz ping loop across reconnects; emits only when live. */
  private startPingLoop(): void {
    if (this.pingTimer !== undefined) return
    const tick = (): void => {
      if (this.channel && this.connected) {
        if (!safeEmit(this.channel, 'ping', {
          clientTime: performance.now() / 1000,
          rttMs: Math.round(this.rtt * 1000),
        })) {
          this.setStatus('reconnecting')
          this.scheduleReconnect()
        }
      }
      this.pingTimer = window.setTimeout(tick, 1000)
    }
    tick()
  }

  public sendInput(input: PlayerInput): void {
    if (!this.channel || !this.connected) return
    this.pendingInputs.push(input)
    if (!safeEmit(this.channel, 'input', input)) this.scheduleReconnect()
  }

  public sendSpawn(spawn: ClientSpawn): void {
    if (!this.channel || !this.connected) return
    if (!safeEmit(this.channel, 'spawn', spawn)) this.scheduleReconnect()
  }

  public sendWeaponDrop(drop: ClientWeaponDrop): void {
    if (!this.channel || !this.connected) return
    if (!safeEmit(this.channel, 'weaponDrop', drop)) this.scheduleReconnect()
  }

  public sendWeaponPickup(pickup: ClientWeaponPickup): void {
    if (!this.channel || !this.connected) return
    if (!safeEmit(this.channel, 'weaponPickup', pickup)) this.scheduleReconnect()
  }

  public consumeEvents(): NetEvent[] {
    return this.events.splice(0)
  }

  public get estimatedServerTime(): number {
    if (this.lastSnapshotAt <= 0) return this.serverTime
    return this.serverTime + (performance.now() / 1000 - this.lastSnapshotAt)
  }

  public getInterpolatedPlayers(
    renderServerTime = this.estimatedServerTime,
  ): PlayerSnapshot[] {
    const targetTime = renderServerTime - this.interpolationDelay
    while (
      this.snapshots.length > 2 &&
      this.snapshots[1].serverTime <= targetTime
    ) {
      this.snapshots.shift()
    }
    const a = this.snapshots[0]
    const b = this.snapshots[1]
    if (!a || !b) return []

    const span = Math.max(b.serverTime - a.serverTime, Number.EPSILON)
    const t = Math.max(0, Math.min(1, (targetTime - a.serverTime) / span))
    const players: PlayerSnapshot[] = []

    for (const [id, from] of a.players) {
      const to = b.players.get(id)
      if (!to || id === this.id) continue
      players.push({
        ...to,
        pos: {
          x: from.pos.x + (to.pos.x - from.pos.x) * t,
          y: from.pos.y + (to.pos.y - from.pos.y) * t,
          z: from.pos.z + (to.pos.z - from.pos.z) * t,
        },
        yaw: from.yaw + (to.yaw - from.yaw) * t,
        pitch: from.pitch + (to.pitch - from.pitch) * t,
      })
    }

    return players
  }

  public disconnect(): void {
    this.closing = true
    if (this.reconnectTimer !== undefined) {
      window.clearTimeout(this.reconnectTimer)
      this.reconnectTimer = undefined
    }
    if (this.pingTimer !== undefined) {
      window.clearTimeout(this.pingTimer)
      this.pingTimer = undefined
    }
    this.setStatus('disconnected')
    const old = this.channel
    this.channel = undefined
    this.closeChannel(old)
  }

  private closeChannel(channel = this.channel): void {
    try {
      channel?.close()
    } catch (error) {
      console.warn('[net] channel close failed:', error)
    }
  }

  private applySnapshot(snapshot: WorldSnapshot): void {
    // The `welcome` packet (which carries our id) rides the same unreliable
    // channel and can be dropped; every snapshot also carries selfId, so use it
    // as a reliable fallback — without our id, every self-exclusion inverts.
    if (snapshot.selfId) this.id = snapshot.selfId
    this.serverTime = snapshot.serverTime
    this.lastSnapshotAt = performance.now() / 1000
    this.lastProcessedInputSeq = snapshot.lastProcessedInputSeq
    while (
      this.pendingInputs.length > 0 &&
      this.pendingInputs[0].seq <= snapshot.lastProcessedInputSeq
    ) {
      this.pendingInputs.shift()
    }

    this.snapshots.push({
      serverTime: snapshot.serverTime,
      players: new Map(snapshot.players.map((player) => [player.id, player])),
    })
    this.latestSelf = snapshot.players.find((player) => player.id === this.id)
    this.latestPlayers = snapshot.players
    if (this.snapshots.length > 64) this.snapshots.shift()
    this.events.push(...snapshot.events)
  }
}
